import React, { useState, useEffect } from 'react';
import { X, Star, ThumbsUp, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { useTheme } from '@/contexts/ThemeContext';
import { SendEmailService } from '@/services/SendEmailService';
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';

interface FeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
    userEmail?: string;
    triggerSource?: string;
    initialMessage?: string;
}

export const FeedbackModal = ({ isOpen, onClose, userEmail, triggerSource = 'Settings', initialMessage = '' }: FeedbackModalProps) => {
    const [rating, setRating] = useState(0);
    const [selectedChips, setSelectedChips] = useState<string[]>([]);
    const [feedbackText, setFeedbackText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const { theme } = useTheme();
    const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

    useEffect(() => {
        if (!isOpen) return;
        setIsSuccess(false);
        setIsSubmitting(false);
        setRating(0);
        setSelectedChips([]);
    }, [isOpen, initialMessage, triggerSource]);

    useEffect(() => {
        if (initialMessage) {
            setFeedbackText(`Regarding message: ${initialMessage.slice(0, 100)}...\n\n`);
        } else {
            setFeedbackText('');
        }
    }, [initialMessage, isOpen]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-close the modal 3 seconds after successful submission
    useEffect(() => {
        if (isSuccess) {
            const timer = setTimeout(() => {
                onClose();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [isSuccess, onClose]);

    const chips = triggerSource === 'Chat Dislike'
        ? ["Too Short", "Too Long", "Inaccurate", "Not Helpful", "Offensive", "Other"]
        : [
            "Overall Experience", "Support & Assistance", "Response Speed",
            "Accuracy & Quality", "Clarity"
        ];

    const toggleChip = (chip: string) => {
        if (selectedChips.includes(chip)) {
            setSelectedChips(selectedChips.filter(c => c !== chip));
        } else {
            setSelectedChips([...selectedChips, chip]);
        }
    };

    const handleSubmit = async () => {
        if (rating === 0) {
            toast({ title: "Rate your experience", description: "Please select a star rating.", variant: "destructive" });
            return;
        }

        setIsSubmitting(true);
        try {
            await SendEmailService.send({
                type: 'feedback',
                userEmail,
                data: {
                    rating,
                    tags: selectedChips,
                    feedback: feedbackText,
                    triggerSource
                },
                clientInfo: {
                    userAgent: navigator.userAgent,
                    platform: (navigator as any).platform || 'unknown',
                    language: navigator.language,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    referrer: document.referrer || 'direct',
                }
            });

            setIsSuccess(true);
        } catch (error: any) {
            console.error('Feedback error:', error);
            toast({ title: "Error", description: error.message || "Failed to send feedback.", variant: "destructive" });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const content = isSuccess ? (
        <div className={`w-full max-w-sm rounded-[32px] p-8 flex flex-col items-center text-center ${isDarkMode ? 'bg-[#18181b] text-white' : 'bg-white text-black'}`}>
            <div className="mt-8 mb-6">
                <ThumbsUp className="w-16 h-16 text-red-500" strokeWidth={1.5} />
            </div>
            {isMobile ? (
                <DrawerTitle className="text-xl font-bold mb-2 text-center">ThankYou</DrawerTitle>
            ) : (
                <h2 className="text-xl font-bold mb-2 text-center">ThankYou</h2>
            )}

            {isMobile ? (
                <DrawerDescription className={`text-sm mb-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Your Feedback was Successfully Submitted
                </DrawerDescription>
            ) : (
                <p className={`text-sm mb-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Your Feedback was Successfully Submitted
                </p>
            )}
        </div>
    ) : (
        <div className={`w-full ${isMobile ? '' : 'max-w-md rounded-[32px] overflow-hidden'} border ${isDarkMode ? 'bg-[#18181b] text-white border-white/10' : 'bg-white text-black border-gray-100/10'}`}>
            {/* Header */}
            <div className="flex items-center gap-4 p-6 pb-2">
                <button onClick={onClose} className="text-2xl leading-none"><ArrowLeft className="w-5 h-5" /></button>
                {isMobile ? (
                    <DrawerTitle className="text-xl font-bold">FeedBack</DrawerTitle>
                ) : (
                    <h2 className="text-xl font-bold">FeedBack</h2>
                )}
                {isMobile ? (
                    <DrawerDescription className="sr-only">Share your thoughts to help us improve.</DrawerDescription>
                ) : (
                    <p className="sr-only">Share your thoughts to help us improve.</p>
                )}
            </div>

            <div className={`p-6 pt-2 ${isMobile ? 'pb-10' : 'max-h-[80vh] overflow-y-auto custom-scrollbar'}`}>
                {/* Rating */}
                <div className="mb-4">
                    <h3 className="text-2xl font-normal mb-1">Rate Your Experience</h3>
                    <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <button key={star} onClick={() => setRating(star)} className="focus:outline-none transition-transform active:scale-95">
                                <Star
                                    className={`w-10 h-10 ${star <= rating ? 'fill-red-600 text-red-600' : isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}
                                />
                            </button>
                        ))}
                    </div>
                </div>

                <hr className={`border-t mb-6 ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`} />

                {/* Chips */}
                <div className="mb-4">
                    <h3 className="text-sm font-bold mb-3">Tell us what can be Improved?</h3>
                    <div className="flex flex-wrap gap-2">
                        {chips.map(chip => (
                            <button
                                key={chip}
                                onClick={() => toggleChip(chip)}
                                className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${selectedChips.includes(chip)
                                    ? 'bg-[red] text-white shadow-md'
                                    : isDarkMode
                                        ? 'bg-[#27272a] text-gray-300 hover:bg-[#3f3f46]'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {chip}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Text Area */}
                <div className="mb-4">
                    <Textarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder="Tell us on how can we improve..."
                        className={`w-full h-36 resize-none rounded-xl p-2 text-sm focus-visible:ring-1 focus-visible:ring-offset-0 ${isDarkMode
                            ? 'bg-transparent border-gray-700 placeholder:text-gray-600 focus-visible:ring-gray-600'
                            : 'bg-white border-gray-200 placeholder:text-gray-300'
                            }`}
                    />
                </div>

                {/* Submit */}
                <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={`w-full py-6 rounded-xl text-md font-bold transition-colors duration-200 
                        ${isDarkMode
                            ? 'bg-white text-black hover:bg-gray-200 disabled:opacity-70'
                            : 'bg-[#2c2c2c] text-white hover:bg-[#3a3a3a] disabled:opacity-70'
                        }` 
                    }
                >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
            </div>
        </div>
    );

    if (isMobile) {
        return (
            <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
                <DrawerContent 
                showHandle={false}
                className={`mx-auto flex w-full max-w-lg flex-col rounded-t-3xl border ${isDarkMode ? 'border-[#333] bg-[#1F1F1F]/95' : 'border-border/40 bg-background/95'} pb-3 shadow-2xl backdrop-blur-lg `}
                >
                    <div className="mx-auto w-full rounded-t-3xl max-w-lg overflow-y-auto no-scrollbar relative">
                        {isSuccess ? <div className="p-8 flex justify-center">{content}</div> : content}
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-sm">
                {content}
            </div>
        </div>
    );
};

